import React from 'react';

const GlobalStyle = ({ C }) => (
	<style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { scroll-behavior: smooth; }

    body {
      background: ${C.bgDark};
      color: #A0B0AE;
      font-family: 'DM Sans', sans-serif;
      font-weight: 300;
      line-height: 1.7;
      -webkit-font-smoothing: antialiased;
      overflow-x: hidden;
      font-size: 18px;
    }

    /* ── MOBILE FONT SCALING ── */
    @media (max-width: 639px) {
      body { font-size: 17px; }

      /* Body copy */
      p, li, td, th { font-size: 1rem; line-height: 1.85; }

      /* Section labels */
      .section-label { font-size: 11px !important; letter-spacing: 0.14em; }

      /* Nav mono labels */
      .nav-link { font-size: 13px !important; }

      /* Buttons */
      .btn-primary { font-size: 13px !important; padding: 16px 28px !important; }
      .btn-outline  { font-size: 13px !important; padding: 15px 27px !important; }

      /* Risk badges */
      .risk-high, .risk-medium, .risk-low { font-size: 12px !important; padding: 3px 10px !important; }

      /* Stat numbers */
      .stat-number { font-size: 42px !important; }

      /* Mobile nav items */
      .mobile-nav-item { font-size: 36px !important; }

      /* Table text */
      table { font-size: 14px !important; }
      th { font-size: 10px !important; }
    }

    ::selection { background: ${C.tealLight}40; color: white; }

    @keyframes fadeUp    { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
    @keyframes fadeIn    { from { opacity:0; } to { opacity:1; } }
    @keyframes pulse     { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    @keyframes slideDown { from { opacity:0; transform:translateY(-12px); } to { opacity:1; transform:translateY(0); } }
    @keyframes spin      { to { transform: rotate(360deg); } }
    @keyframes glow      { 0%,100% { opacity:0.5; } 50% { opacity:1; } }

    .fade-up { animation: fadeUp  0.7s cubic-bezier(.22,.68,0,1.2) both; }
    .fade-in { animation: fadeIn  0.6s ease both; }
    .delay-1 { animation-delay: 0.10s; }
    .delay-2 { animation-delay: 0.22s; }
    .delay-3 { animation-delay: 0.36s; }
    .delay-4 { animation-delay: 0.50s; }
    .delay-5 { animation-delay: 0.64s; }

    a { color: inherit; text-decoration: none; }

    /* ── NAV ── */
    .nav-link {
      font-family: 'DM Mono', monospace; font-size: 14px;
      letter-spacing: 0.12em; text-transform: uppercase;
      color: #4A6A66; transition: color 0.2s;
      position: relative; padding-bottom: 2px;
    }
    .nav-link::after {
      content: ''; position: absolute; bottom: 0; left: 0;
      width: 0; height: 1px; background: ${C.teal}; transition: width 0.25s;
    }
    .nav-link:hover, .nav-link.active { color: ${C.tealLight}; }
    .nav-link:hover::after, .nav-link.active::after { width: 100%; }

    /* ── BUTTONS ── */
    .btn-primary {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      background: ${C.teal}; color: white;
      font-family: 'DM Mono', monospace; font-size: 14px;
      letter-spacing: 0.1em; text-transform: uppercase;
      padding: 14px 28px; border: none; cursor: pointer;
      transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
      white-space: nowrap;
    }
    .btn-primary:hover {
      background: ${C.tealDark};
      transform: translateY(-1px);
      box-shadow: 0 6px 20px ${C.teal}40;
    }
    .btn-outline {
      display: inline-flex; align-items: center; justify-content: center; gap: 8px;
      background: transparent; color: ${C.tealLight};
      font-family: 'DM Mono', monospace; font-size: 11px;
      letter-spacing: 0.1em; text-transform: uppercase;
      padding: 13px 27px; border: 1px solid ${C.teal}80; cursor: pointer;
      transition: all 0.2s; white-space: nowrap;
    }
    .btn-outline:hover { background: ${C.teal}20; border-color: ${C.tealLight}; color: white; }

    /* ── SECTION LABEL ── */
    .section-label {
      font-family: 'DM Mono', monospace; font-size: 10px;
      letter-spacing: 0.18em; text-transform: uppercase; color: ${C.tealLight};
      display: flex; align-items: center; gap: 10px;
    }
    .section-label::before {
      content: ''; display: inline-block;
      width: 24px; height: 1px; background: ${C.teal};
    }

    /* ── HEADINGS ── */
    .display-heading {
      font-family: 'Playfair Display', serif;
      font-weight: 700; line-height: 1.1; color: white;
    }

    /* ── DIVIDER ── */
    .divider { width: 100%; height: 1px; background: #1E3230; }

    /* ── RISK BADGES ── */
    .risk-high   { color: #E07070; background: #2A1818; padding: 2px 8px; font-size: 11px; font-family: 'DM Mono', monospace; }
    .risk-medium { color: #C88A50; background: #221A10; padding: 2px 8px; font-size: 11px; font-family: 'DM Mono', monospace; }
    .risk-low    { color: #6DBF67; background: #0E1F0E; padding: 2px 8px; font-size: 11px; font-family: 'DM Mono', monospace; }

    /* ── STAT NUMBER ── */
    .stat-number {
      font-family: 'Playfair Display', serif;
      font-weight: 600; color: ${C.tealLight}; line-height: 1;
    }

    /* ── MOBILE MENU ── */
    .mobile-overlay {
      position: fixed; inset: 0; z-index: 200;
      background: #0A1210;
      display: flex; flex-direction: column;
      padding: 88px 32px 48px;
      animation: slideDown 0.28s ease both;
    }
    .mobile-nav-item {
      font-family: 'Playfair Display', serif; font-size: 32px; font-weight: 600;
      color: #4A6A66; padding: 18px 0; border-bottom: 1px solid #1E3230;
      display: block; transition: color 0.2s;
    }
    .mobile-nav-item:hover, .mobile-nav-item.active { color: ${C.tealLight}; }

    /* ── CARDS / SURFACES ── */
    .surface {
      background: #111E1C;
      border: 1px solid #1E3230;
    }
    .surface-raised {
      background: #162220;
      border: 1px solid #243A36;
    }

    /* ── TABLES ── */
    table { border-collapse: collapse; width: 100%; }
    th { color: ${C.tealLight}; }
    tr { border-bottom: 1px solid #1A2E2A; }
    tr:hover { background: #111E1C; }

    /* ── SCROLLBAR ── */
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: #0A1210; }
    ::-webkit-scrollbar-thumb { background: #1E3230; border-radius: 3px; }
    ::-webkit-scrollbar-thumb:hover { background: ${C.teal}; }
  `}</style>
);

export default GlobalStyle;
